import { supabase } from '@/utils/supabase';
import type {
  CleanApprovalAdminState,
  CleanApprovalPreview,
  CleanApprovalSendResult,
  CleanApprovalState
} from '@/types/clean-approval';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

interface ApiErrorBody {
  code?: string;
  error?: string;
  staleReason?: 'renderer_changed' | 'content_changed';
  currentRevision?: number;
  currentPreview?: CleanApprovalPreview;
  saved?: boolean;
  revision?: number;
}
export class CleanApprovalApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: ApiErrorBody;

  constructor(message: string, code: string, status: number, details: ApiErrorBody) {
    super(message);
    this.name = 'CleanApprovalApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

async function invoke<T>(body: Record<string, unknown>): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new CleanApprovalApiError('Your session has expired. Please sign in again.', 'UNAUTHORIZED', 401, {});
  }

  const response = await fetch(`${SUPABASE_URL}/functions/v1/clean-approval`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      apikey: SUPABASE_ANON_KEY
    },
    body: JSON.stringify(body)
  });
  const payload = await response.json().catch(() => ({})) as ApiErrorBody;
  if (!response.ok) {
    throw new CleanApprovalApiError(
      payload.error || 'Clean Approval request failed',
      payload.code || 'REQUEST_FAILED',
      response.status,
      payload
    );
  }
  return payload as T;
}

export const cleanApprovalService = {
  getState(loanId: string): Promise<CleanApprovalState> {
    return invoke({ action: 'get-state', loanId });
  },

  saveAndPreview(
    loanId: string,
    expectedRevision: number,
    includedConditionInstanceIds: string[]
  ): Promise<CleanApprovalPreview> {
    return invoke({
      action: 'save-and-preview',
      loanId,
      expectedRevision,
      includedConditionInstanceIds
    });
  },

  previewCurrent(loanId: string, revision: number): Promise<CleanApprovalPreview> {
    return invoke({ action: 'preview-current', loanId, revision });
  },

  send(
    loanId: string,
    revision: number,
    previewFingerprint: string
  ): Promise<CleanApprovalSendResult> {
    return invoke({ action: 'send', loanId, revision, previewFingerprint });
  },

  getAdminState(): Promise<CleanApprovalAdminState> {
    return invoke({ action: 'get-admin-state' });
  },

  setRuleStatus(input: {
    externalId: string;
    representativeTitle: string;
    status: 'approved' | 'rejected' | 'disabled';
    reason?: string;
  }): Promise<{ success: true }> {
    return invoke({ action: 'set-rule-status', ...input });
  },

  setFullyAutomatic(enabled: boolean): Promise<{ success: true }> {
    return invoke({ action: 'set-fully-automatic', enabled });
  }
};

export function downloadCleanApprovalPdf(pdfBase64: string, filename: string): void {
  const binary = atob(pdfBase64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function cleanApprovalPdfObjectUrl(pdfBase64: string): string {
  const binary = atob(pdfBase64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
}
